import { useState, useEffect, useRef } from 'react'
import styles from './NotificationsPanel.module.css'
import { api } from '../lib/api'

type NotifTier = 'critical' | 'important' | 'informational'

const TIER_COLOR: Record<NotifTier, string> = {
  critical:      'var(--re, #f87171)',
  important:     'var(--ye, #fbbf24)',
  informational: 'var(--ac, #38bdf8)',
}

const TIER_LABEL: Record<NotifTier, string> = {
  critical:      '!',
  important:     '▲',
  informational: '·',
}

interface Notification {
  id: string
  message: string
  entity_type: string | null
  entity_id: string | null
  read: boolean
  created_at: string
  tier?: NotifTier
}

const ENTITY_ICONS: Record<string, string> = {
  lead: '⚡',
  deal: '📋',
  user: '👤',
  permission: '🔑',
  system: '⚙️',
}

export default function NotificationsPanel() {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const unreadCount = notifications.filter(n => !n.read && (n.tier === 'critical' || n.tier === 'important' || !n.tier)).length

  async function fetchNotifications() {
    setLoading(true)
    try {
      const data = await api.get('/api/auth/notifications') as Notification[]
      setNotifications(data)
    } catch (_) {
      // Gracefully degrade — backend may not be running in dev
      setNotifications(DEMO_NOTIFICATIONS)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) fetchNotifications()
  }, [open])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  async function markRead(id: string) {
    try {
      await api.patch(`/api/auth/notifications/${id}/read`, {})
    } catch (_) {}
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }

  async function markAllRead() {
    try {
      await api.post('/api/auth/notifications/read-all', {})
    } catch (_) {}
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  function formatTime(iso: string) {
    const d = new Date(iso)
    const now = new Date()
    const diff = (now.getTime() - d.getTime()) / 1000
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return d.toLocaleDateString()
  }

  return (
    <div className={styles.wrapper} ref={panelRef}>
      <button
        className={styles.bell}
        onClick={() => setOpen(o => !o)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        🔔
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Notifications</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {unreadCount > 0 && (
                <button className={styles.markAllBtn} onClick={markAllRead}>
                  Mark all read
                </button>
              )}
              <a
                href="/app/admin"
                onClick={e => { e.preventDefault(); setOpen(false); window.location.href = '/app/admin' }}
                style={{ fontSize: 11, color: 'var(--tx2, #56788e)', textDecoration: 'none' }}
                title="Notification settings"
              >
                ⚙
              </a>
            </div>
          </div>

          <div className={styles.list}>
            {loading && <div className={styles.loading}>Loading…</div>}
            {!loading && notifications.length === 0 && (
              <div className={styles.empty}>No notifications</div>
            )}
            {!loading && notifications.map(n => {
              const tier = n.tier ?? 'informational'
              const tierColor = TIER_COLOR[tier]
              return (
                <div
                  key={n.id}
                  className={`${styles.notif} ${!n.read ? styles.unread : ''}`}
                  onClick={() => !n.read && markRead(n.id)}
                  role={!n.read ? 'button' : undefined}
                  tabIndex={!n.read ? 0 : undefined}
                  onKeyDown={e => e.key === 'Enter' && !n.read && markRead(n.id)}
                  style={tier === 'critical' && !n.read ? {
                    borderLeft: `3px solid ${tierColor}`,
                  } : undefined}
                >
                  <span
                    className={styles.notifIcon}
                    title={tier}
                    style={{ position: 'relative' }}
                  >
                    {ENTITY_ICONS[n.entity_type || 'system'] ?? '🔔'}
                    {tier !== 'informational' && (
                      <span style={{
                        position: 'absolute', top: -2, right: -2,
                        width: 10, height: 10, borderRadius: '50%',
                        background: tierColor, fontSize: 7, lineHeight: '10px',
                        textAlign: 'center', color: '#060d19', fontWeight: 900,
                      }}>
                        {TIER_LABEL[tier]}
                      </span>
                    )}
                  </span>
                  <div className={styles.notifBody}>
                    <p className={styles.notifMsg}>{n.message}</p>
                    <span className={styles.notifTime}>
                      {formatTime(n.created_at)}
                      {' · '}
                      <span style={{ color: tierColor, fontSize: 10, fontWeight: 600 }}>{tier}</span>
                    </span>
                  </div>
                  {!n.read && <span className={styles.unreadDot} aria-hidden="true" />}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Demo fallback when backend isn't running
const DEMO_NOTIFICATIONS: Notification[] = [
  { id: '1', message: '7,545 leads imported from Overture + NPPES sources', entity_type: 'lead', entity_id: null, read: false, created_at: new Date(Date.now() - 3600000).toISOString(), tier: 'informational' },
  { id: '2', message: 'New deal created: Mercy Urgent Care (healthcare)', entity_type: 'deal', entity_id: 'd1', read: false, created_at: new Date(Date.now() - 7200000).toISOString(), tier: 'important' },
  { id: '3', message: 'ArkBest Corp moved to Negotiation stage', entity_type: 'deal', entity_id: 'd4', read: true, created_at: new Date(Date.now() - 86400000).toISOString(), tier: 'important' },
  { id: '4', message: 'Role permission updated: sales_rep gained lead_engine.edit', entity_type: 'permission', entity_id: null, read: true, created_at: new Date(Date.now() - 172800000).toISOString(), tier: 'critical' },
]
